import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  HandHeart,
  Compass,
  Clock,
  Calendar,
  MapPin,
  Landmark,
  BookOpen,
  Sparkles,
  Flower2,
  ScrollText,
  Music4,
  Building2,
  GraduationCap,
  PlayCircle,
} from "lucide-react";
import PageLayout from "../components/layout/PageLayout";
import SectionHeading from "../components/shared/SectionHeading";
import SectionDivider from "../components/decor/SectionDivider";
import Mandala from "../components/decor/Mandala";
import CTASection from "../components/shared/CTASection";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { IMG, SITE, HIGHLIGHTS, TEMPLE_TIMINGS, CORE_VALUES, OFFICIAL } from "../data/site";
import { DEITIES } from "../data/deities";
import { YOUTUBE_CHANNEL_URL, ytThumbnail } from "../data/youtube";
import { useLang } from "../i18n/LanguageContext";
import { fetchLatestYoutubeVideos, fetchRecentEvents } from "../api";

const iconMap = { Landmark, Flower2, ScrollText, HandHeart, Music4, Sparkles };
const HOME_VIDEO_COUNT = 4;

const quickAccess = [
  { icon: Compass, label: "Parampara", labelKn: "ಗುರುಪರಂಪರೆ", to: "/lineage?tab=guru" },
  { icon: Landmark, label: "Deities", labelKn: "ದೇವರುಗಳು", to: "/deities" },
  { icon: HandHeart, label: "Sevas", labelKn: "ಸೇವೆಗಳು", to: "/sevas" },
  { icon: Calendar, label: "Events", labelKn: "ಕಾರ್ಯಕ್ರಮಗಳು", to: "/events" },
  { icon: MapPin, label: "Branches", labelKn: "ಶಾಖೆಗಳು", to: "/branches" },
  { icon: BookOpen, label: "Publications", labelKn: "ಪ್ರಕಟಣೆಗಳು", to: "/publications" },
];

const Home = () => {
  const { t, lang } = useLang();
  const pickDeity = (d, key) => (lang === "kn" && d[`${key}Kn`]) || d[key];
  const featuredDeities = DEITIES.slice(0, 4);
  const HERO_BANNERS = [
    {
      desktop: IMG.heroTemple,
      mobile: require("../assets/new-banner-image-with-lake-mobile.png"),
      desktopPosition: "center top",
      mobilePosition: "center center",
    }
    // {
    //   desktop: require("../assets/vv7umc9m_second_banner_newtemple.webp"),
    //   mobile: require("../assets/mobile-hero-temple-2.png"),
    //   desktopPosition: "center top",
    //   mobilePosition: "top top",
    // },
  ];
  const [heroIndex, setHeroIndex] = useState(0);
  // useEffect(() => {
  //   const id = setInterval(() => setHeroIndex((i) => (i + 1) % HERO_BANNERS.length), 6000);
  //   return () => clearInterval(id);
  //   // eslint-disable-next-line
  // }, []);
  const [recentEvents, setRecentEvents] = useState([]);
  const [recentEventsLoading, setRecentEventsLoading] = useState(true);
  const [recentEventsError, setRecentEventsError] = useState("");
  const [pillarModal, setPillarModal] = useState(null);
  const [videos, setVideos] = useState([]);

  useEffect(() => {
    let alive = true;

    const loadRecentEvents = async () => {
      try {
        const events = await fetchRecentEvents({ size: 15, index: 0 });
        if (!alive) return;
        setRecentEvents(events.slice(0, 4));
        setRecentEventsError("");
      } catch (error) {
        if (!alive) return;
        setRecentEventsError("Unable to load recent events right now.");
        if (process.env.NODE_ENV !== "production") {
          console.warn("Unable to load recent events.", error);
        }
      } finally {
        if (alive) {
          setRecentEventsLoading(false);
        }
      }
    };

    loadRecentEvents();

    return () => { alive = false; };
  }, []);

  useEffect(() => {
    let alive = true;

    const loadVideos = async () => {
      try {
        const latestVideos = await fetchLatestYoutubeVideos({ maxResults: HOME_VIDEO_COUNT });
        if (!alive) return;
        setVideos(latestVideos);
      } catch (error) {
        if (!alive) return;
        if (process.env.NODE_ENV !== "production") {
          console.warn("Unable to load latest YouTube videos.", error);
        }
      }
    };

    loadVideos();

    return () => { alive = false; };
  }, []);

  return (
    <PageLayout>
      {/* HERO */}
      <section className="relative min-h-[86vh] sm:min-h-[88vh] lg:min-h-[92vh] flex items-start overflow-hidden" data-testid="hero-section">
        {/* Background carousel - fade between images every 6s */}
        {HERO_BANNERS.map((banner, i) => (
          <React.Fragment key={banner.desktop}>
            <div
              className={`absolute inset-0 bg-cover scale-105 transition-opacity duration-[1400ms] ease-in-out sm:hidden ${heroIndex === i ? "opacity-100" : "opacity-0"}`}
              style={{ backgroundImage: `url('${banner.mobile}')`, backgroundPosition: banner.mobilePosition }}
              data-testid={`hero-slide-mobile-${i}`}
              aria-hidden={heroIndex !== i}
            />
            <div
              className={`absolute inset-0 hidden bg-cover scale-105 transition-opacity duration-[1400ms] ease-in-out sm:block ${heroIndex === i ? "opacity-100" : "opacity-0"}`}
              style={{ backgroundImage: `url('${banner.desktop}')`, backgroundPosition: banner.desktopPosition }}
              data-testid={`hero-slide-${i}`}
              aria-hidden={heroIndex !== i}
            />
          </React.Fragment>
        ))}
        {/* Slide indicators */}
        <div className="absolute bottom-6 left-1/2 -translate-x-1/2 z-30 flex gap-2" data-testid="hero-indicators">
          {HERO_BANNERS.map((_, i) => (
            <button
              key={i}
              onClick={() => setHeroIndex(i)}
              aria-label={`Go to slide ${i + 1}`}
              className={`h-1.5 rounded-full transition-all ${heroIndex === i ? "w-8 bg-[#D4AF37]" : "w-4 bg-[#FBF7F0]/60 hover:bg-[#D4AF37]/70"}`}
              data-testid={`hero-dot-${i}`}
            />
          ))}
        </div>

        {/* Mandala backdrop */}
        <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 pointer-events-none">
          <div className="animate-mandala-slow">
            <Mandala size={720} color="#D4AF37" opacity={0.2} />
          </div>
        </div>
        <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 pointer-events-none">
          <div className="animate-mandala-reverse">
            <Mandala size={480} color="#D4AF37" opacity={0.15} />
          </div>
        </div>

        <div className="relative w-full max-w-7xl mx-auto px-4 lg:px-8 pt-16 sm:pt-20 lg:pt-24 pb-8 text-center">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-[#B48F30] bg-[#D4AF37] text-[#3B2A1A] homebanner tracking-widest uppercase mb-4 font-semibold shadow-lg">
            {lang === "kn" ? "ತ್ರೈಲೋಕ್ಯಗುರು ಶ್ರೀಮನ್ ಮಧ್ವಾಚಾರ್ಯ ಮೂಲ ಮಹಾಸಂಸ್ಥಾನಂ" : "Trailokyaguru Sriman Madhwaacharya Moola Mahasamsthanam"}
          </div>
          <h1
            className="font-devanagari-en text-[34px] sm:text-8xl lg:text-9xl xl:text-[120px] leading-[1.02] tracking-tight whitespace-nowrap not-italic"
            style={{
              color: "#CC5500",
              fontWeight: 400,
              fontStyle: "normal",
              marginTop: 20
            }}
          >
            {lang === "kn" ? "ಶ್ರೀ ಸೋದೆ ವಾದಿರಾಜ ಮಠ" : "Sri Sode Vadiraja Matha"} 
          </h1>
          <p
            className="mt-6 sm:mt-8 font-kannada max-w-4xl mx-auto text-sm text-lg sm:text-xl lg:text-2xl leading-relaxed"
            style={{
              color: "#FFFFFF",
              fontWeight: 700,
              textShadow:
                "0 2px 4px rgba(0,0,0,0.85), 0 4px 12px rgba(0,0,0,0.75), 0 0 24px rgba(0,0,0,0.55), 1px 1px 0 #3B2A1A, -1px -1px 0 #3B2A1A, 1px -1px 0 #3B2A1A, -1px 1px 0 #3B2A1A",
            }}
            data-testid="hero-kannada"
          >
            ಶ್ರೀ ಭಾವಿಸಮೀರ ಶ್ರೀಮದ್ವಾದಿರಾಜರು ಗುರುಸಾರ್ವಭೌಮರ ಮೂಲ ಪಂಚವೃಂದಾವನ ಸನ್ನಿಧಿ
          </p>
          <div className="mt-48 sm:mt-56 lg:mt-72 flex flex-wrap justify-center gap-3 sm:gap-4">
            <Link
              to="/about"
              className="inline-flex items-center justify-center gap-2 rounded-full bg-[#D4AF37] text-[#3B2A1A] px-5 py-3 text-sm sm:px-8 sm:py-4 sm:text-base font-bold hover:bg-[#B48F30] transition shadow-2xl ring-2 ring-white/30 hover:ring-white/60 min-w-[160px] sm:min-w-[220px]"
              data-testid="hero-explore-btn"
            >
              {t("home.explore.eyebrow")} <ArrowRight className="w-4 h-4 sm:w-5 sm:h-5" />
            </Link>
            <Link
              to="/sevas"
              className="inline-flex items-center justify-center gap-2 rounded-full bg-[#B65A14] text-[#FBF7F0] px-5 py-3 text-sm sm:px-8 sm:py-4 sm:text-base font-bold hover:bg-[#8F4610] transition shadow-2xl ring-2 ring-white/30 hover:ring-white/60 min-w-[160px] sm:min-w-[220px]"
              data-testid="hero-seva-btn"
            >
              <HandHeart className="w-4 h-4 sm:w-5 sm:h-5" /> {t("nav.bookSeva")}
            </Link>
          </div>

          {/* Scroll hint */}
          <div className="mt-8 flex flex-col items-center gap-1.5 text-[#D4AF37] animate-soft-pulse">
            <span className="overline text-[10px]">Scroll to Explore</span>
            <div className="w-px h-8 bg-gradient-to-b from-[#D4AF37] to-transparent" />
          </div>
        </div>
      </section>

      {/* QUICK ACCESS */}
      <section className="relative -mt-10 sm:-mt-14 z-10" data-testid="quick-access">
        <div className="max-w-6xl mx-auto px-4 lg:px-8">
          <div className="bg-[#FBF7F0] border border-[#D4AF37]/30 rounded-3xl shadow-2xl p-4 sm:p-6">
            <div className="grid grid-cols-3 sm:grid-cols-6 gap-3 sm:gap-4">
              {quickAccess.map((q) => (
                <Link
                  key={q.label}
                  to={q.to}
                  className="group flex flex-col items-center text-center gap-3 py-4 px-3 rounded-2xl border border-transparent hover:border-[#D4AF37]/40 hover:bg-[#F2E7D5]/40 transition"
                  data-testid={`quick-${q.label.toLowerCase().replace(/\s/g, "-")}`}
                >
                  <span className="w-16 h-16 sm:w-[72px] sm:h-[72px] rounded-full border border-[#D4AF37]/40 flex items-center justify-center bg-gradient-to-br from-[#F2E7D5]/60 to-[#FBF7F0] text-[#B65A14] group-hover:from-[#D4AF37]/20 group-hover:to-[#F2E7D5] transition">
                    <q.icon className="w-8 h-8 sm:w-9 sm:h-9" />
                  </span>
                  <span className="font-serif-display text-base sm:text-lg text-[#3B2A1A] font-semibold leading-tight">{lang === "kn" ? q.labelKn : q.label}</span>
                </Link>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ABOUT */}
      <section className="py-16 sm:py-32 relative" data-testid="home-about">
        <div className="max-w-7xl mx-auto px-4 lg:px-8 grid lg:grid-cols-2 gap-14 items-center">
          <div className="relative">
            <div className="aspect-[4/5] rounded-3xl overflow-hidden shadow-2xl">
              <img src={require("../assets/tj7mr5xm_sode_kshetra_Enhanced.webp")} alt="Sri Sode Kshetra - temple courtyard with dhwaja stambha" className="w-full h-full object-cover" data-testid="legacy-image" />
            </div>
            <div className="absolute -bottom-6 -right-6 hidden sm:block bg-[#D4AF37] text-[#3B2A1A] p-6 rounded-2xl shadow-xl max-w-[260px]">
              <div className="overline text-[#3B2A1A]/80 mb-1">{ lang === 'kn' ? '13ನೇ ಶತಮಾನದಲ್ಲಿ ಸ್ಥಾಪಿತ' : 'Est. 13th Century'}</div>
              <p className="font-serif-display text-2xl leading-tight text-[#000000] font-bold">{ lang === 'kn' ? 'ಉಡುಪಿಯ ಎಂಟು ಪವಿತ್ರ ಅಷ್ಟಮಠಗಳಲ್ಲಿ ಒಂದು.' : 'One of the eight sacred Ashta Mathas of Udupi.'}</p>
            </div>
            <div className="absolute -top-6 -left-6 pointer-events-none opacity-70">
              <Mandala size={140} color="#D4AF37" opacity={0.5} />
            </div>
          </div>

          <div>
            <SectionHeading
              eyebrow={t("home.hero.eyebrow")}
              title={t("home.hero.title")}
              subtitle={t("home.hero.subtitle")}
            />
            <p className={`mt-5 text-base sm:text-lg leading-relaxed`}  style={{textAlign: "justify"}}>
              {lang === "kn" ? (
                "ಶ್ರೀ ಸೋದೆ ವಾದಿರಾಜ ಮಠವು ಏಳು ಶತಮಾನಗಳಷ್ಟು ಪ್ರಾಚೀನವಾದ ಆಧ್ಯಾತ್ಮಿಕ ಮತ್ತು ಬೌದ್ಧಿಕ ಪರಂಪರೆಯನ್ನು ಹೊಂದಿದ್ದು, ಶ್ರೀ ಮಧ್ವಾಚಾರ್ಯರ ಸಹೋದರರಾದ ಶ್ರೀ ವಿಷ್ಣುತೀರ್ಥಾಚಾರ್ಯರ ಪರಂಪರೆಯಲ್ಲಿ ಬೇರೂರಿದೆ. ಈ ಮಹೋನ್ನತ ಪರಂಪರೆಯು ಮುಂದೆ ಶ್ರೀ ಭವಿಷ್ಯಮೀರ ವಾದಿರಾಜ ತೀರ್ಥರ ಅವತರಣದಿಂದ ಮತ್ತಷ್ಟು ಶ್ರೀಮಂತವಾಯಿತು. ತಮ್ಮ ಆಳವಾದ ಜ್ಞಾನ, ಭಕ್ತಿ ಹಾಗೂ ಅಮೂಲ್ಯ ಸಾಹಿತ್ಯ ಕೃತಿಗಳ ಮೂಲಕ ಅವರು ಈ ಪರಂಪರೆಗೆ ಅಪಾರ ಕೊಡುಗೆಯನ್ನು ನೀಡಿದ್ದಾರೆ."
              ) : (
                <>
                  Sri Sode Vadiraja Matha carries a seven-century-old spiritual and intellectual legacy, rooted in the lineage of{" "}
                  <strong className="font-bold px-1 rounded">Sri Vishnu Tirthacharya</strong>, the brother of Sri Madhvacharya. This illustrious lineage was further enriched by the advent of{" "}
                  <strong className="font-bold px-1 rounded">Sri Bhavishameera Vadiraja Tirtharu</strong>, who contributed immensely to the tradition through his profound knowledge, devotion, and literary works.
                </>
              )}
            </p>
            <p className={`mt-5 text-base sm:text-lg leading-relaxed`}  style={{textAlign: "justify"}}>
              {lang === "kn" ? (
                "ಶತಮಾನಗಳ ಅವಧಿಯಲ್ಲಿ, ಈ ಪರಂಪರೆಯಲ್ಲಿ ಅನೇಕ ಮಹಾನ್ ವಿದ್ವಾಂಸರು ಹಾಗೂ ಆಧ್ಯಾತ್ಮಿಕ ಮಹನೀಯರು ಉದಯಿಸಿ, ತಮ್ಮ ಜ್ಞಾನ, ಅನುಭವ ಮತ್ತು ಸಮರ್ಪಿತ ಸೇವೆಯ ಮೂಲಕ ಪರಂಪರೆಯನ್ನು ಮತ್ತಷ್ಟು ಶ್ರೀಮಂತಗೊಳಿಸಿದ್ದಾರೆ. ಇವರಲ್ಲಿ ಅನೇಕರು ಪ್ರಮುಖ ತಾತ್ತ್ವಿಕ ಮತ್ತು ಸಾಹಿತ್ಯಿಕ ಕೃತಿಗಳ ರಚನೆಯಲ್ಲಿ ಅಪಾರ ಪಾಂಡಿತ್ಯವನ್ನು ಮೆರೆದಿದ್ದು, ಮುಂದಿನ ಪೀಳಿಗೆಗಳಿಗೆ ಅಮೂಲ್ಯವಾದ ಜ್ಞಾನಸಂಪತ್ತನ್ನು ಬಿಟ್ಟುಹೋಗಿದ್ದಾರೆ."
              ) : (
                <>
                  Over the centuries, this lineage has witnessed the emergence of numerous great scholars and spiritual luminaries who have enriched the tradition through their wisdom and dedicated service. Many among them have also been accomplished{" "}
                  <strong className="font-bold px-1 rounded">authors of important philosophical and literary works</strong>, leaving behind a rich body of knowledge for future generations.
                </>
              )}
            </p>
            <p className={`mt-5 text-base sm:text-lg leading-relaxed`}  style={{textAlign: "justify"}}>
              {lang === "kn" ? (
                "ಇಂದು ಈ ಗೌರವಾನ್ವಿತ ಹಾಗೂ ಪ್ರಾಚೀನ ಪರಂಪರೆಯು ಶ್ರೀ ಸೋದೆ ವಾದಿರಾಜ ಮಠದ ಪ್ರಸ್ತುತ ಪೀಠಾಧಿಪತಿಗಳಾದ ಶ್ರೀ ವಿಶ್ವವಲ್ಲಭ ತೀರ್ಥರ ಮಾರ್ಗದರ್ಶನದಲ್ಲಿ ನಿರಂತರವಾಗಿ ಮುಂದುವರಿಯುತ್ತಿದೆ."
              ) : (
                <>
                  Today, this venerable tradition continues under the guidance of{" "}
                  <strong className="font-bold px-1 rounded">Sri Vishwavallabha Tirtharu</strong>, the present Peethadhipathi of Sri Sode Vadiraja Matha.
                </>
              )}
            </p>
            <Link
              to="/about"
              className="mt-10 inline-flex items-center gap-2 text-[#B65A14] font-semibold border-b border-[#B65A14]/40 pb-1 hover:border-[#B65A14] transition"
              data-testid="about-know-more"
            >
              {lang === "kn" ? "ನಮ್ಮ ಪರಂಪರೆಯನ್ನು ತಿಳಿಯಿರಿ" : "Know Our Heritage"} <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* HIGHLIGHTS STRIP */}
      {/* <section className="bg-[#B65A14] text-[#FBF7F0] relative overflow-hidden" data-testid="highlights-strip">
        <div className="absolute inset-0 opacity-10 pointer-events-none">
          <div className="absolute -top-20 -left-20 animate-mandala-slow"><Mandala size={280} color="#D4AF37" opacity={1} /></div>
          <div className="absolute -bottom-20 -right-20 animate-mandala-reverse"><Mandala size={280} color="#D4AF37" opacity={1} /></div>
        </div>
        <div className="relative max-w-7xl mx-auto px-4 lg:px-8 py-14">
          <div className="grid grid-cols-2 md:grid-cols-6 gap-8 text-center">
            {HIGHLIGHTS.map((h) => (
              <div key={h.label}>
                <div className="font-serif-display text-3xl sm:text-4xl text-[#D4AF37]">{(lang === "kn" && h.valueKn) || h.value}</div>
                <div className="text-xs uppercase tracking-widest text-[#F2E7D5]/85 mt-2 leading-tight">{(lang === "kn" && h.labelKn) || h.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section> */}

      <SectionDivider variant="mandala" />

      {/* PONTIFFS TRIBUTE */}
      <section className="max-w-7xl mx-auto px-4 lg:px-8" data-testid="pontiffs-section">
        <SectionHeading
          eyebrow={lang === "kn" ? "ಮಾರ್ಗದರ್ಶಕ ಜ್ಯೋತಿ" : "The Guiding Light"}
          title={lang === "kn" ? "ನಮ್ಮ ಪೀಠಾಧಿಪತಿಗಳು" : "Our Peethadhipathis"}
          subtitle={lang === "kn" ? "ಭಕ್ತಿ, ವಿದ್ವತ್ ಮತ್ತು ಸೇವೆಯ ಜೀವಂತ ಪರಂಪರೆ - ಶ್ರೀ ಸೋದೆ ವಾದಿರಾಜ ಮಠದ ಪೂರ್ವ ಮತ್ತು ಪ್ರಸ್ತುತ ಪೀಠಾಧಿಪತಿಗಳು." : "A living lineage of devotion, scholarship and service - the previous and current Pontiffs of Sri Sode Vadiraja Matha."}
          align="center"
        />
        <div className="mt-14 grid md:grid-cols-2 gap-6 lg:gap-8">
          {[
            {
              testid: "pontiff-previous",
              badge: lang === "kn" ? "ಪೂರ್ವ ಪೀಠಾಧಿಪತಿಗಳು" : "Previous Peethadhipathi",
              years: "1934 - 2007",
              name: lang === "kn" ? "ಶ್ರೀ ಶ್ರೀ ವಿಶ್ವೋತ್ತಮ ತೀರ್ಥ ಸ್ವಾಮಿಜಿ" : "H.H. Sri Vishwothama Theertha Swamiji",
              title: lang === "kn" ? "ಗೌರವಾನ್ವಿತ ಪೂರ್ವಸೂರಿ" : "The revered predecessor",
              image: require("../assets/SriVishwothamaTheerthaSwamiji.png"),
              text: lang === "kn"
                ? "ಶ್ರೀ ಶ್ರೀ ವಿಶ್ವೋತ್ತಮ ತೀರ್ಥ ಸ್ವಾಮಿಜಿ (೧೯೩೪-೨೦೦೭) ಸೋದೆ ವಾದಿರಾಜ ಮಠದ ಅತ್ಯಂತ ಗೌರವಾನ್ವಿತ ಪೀಠಾಧಿಪತಿಗಳಲ್ಲಿ ಒಬ್ಬರು. ತಮ್ಮ ಆಳವಾದ ವಿದ್ವತ್, ಅಚಲ ಶಿಸ್ತು ಮತ್ತು ಆಧ್ಯಾತ್ಮಿಕ ವಿವೇಕಕ್ಕಾಗಿ ಪ್ರಸಿದ್ಧರು. ಚಿಕ್ಕ ವಯಸ್ಸಿಗೆ ಪೀಠಾರೋಹಣ ಮಾಡಿ, ಆರು ದಶಕಗಳಿಗೂ ಹೆಚ್ಚು ಕಾಲ ಶಿಕ್ಷಣ, ಗ್ರಂಥ ಸಂರಕ್ಷಣೆ, ದೇವಸ್ಥಾನ ಪುನರುದ್ಧಾರ ಮತ್ತು ಭಾವಿಸಮೀರ ಗುರುಕುಲದಂತಹ ಸಂಸ್ಥೆಗಳ ಸ್ಥಾಪನೆಯ ಮೂಲಕ ದ್ವೈತ ಸಿದ್ಧಾಂತದ ಸಂರಕ್ಷಣೆ ಮತ್ತು ಪ್ರಚಾರಕ್ಕೆ ಸಮರ್ಪಿಸಿಕೊಂಡರು. ಅವರ ಕರುಣಾಮಯ ಮಾರ್ಗದರ್ಶನ, ಮಂತ್ರಾಕ್ಷತೆಯ ಆಶೀರ್ವಾದ ಹಾಗೂ ವಿನಮ್ರ ಶಾಂತ ಸ್ವಭಾವವು ಅಸಂಖ್ಯ ಭಕ್ತರಿಗೆ ಆಶಾಕಿರಣವಾಯಿತು. ಶ್ರೀ ವಾದಿರಾಜ ತೀರ್ಥರ ಪವಿತ್ರ ಪರಂಪರೆಗಳ ಸಂರಕ್ಷಣೆ, ಆಧ್ಯಾತ್ಮಿಕ ಶಿಕ್ಷಣ ಮತ್ತು ಗೋಶಾಲಾ ಅಭಿವೃದ್ಧಿಗೆ ಅವರ ಕೊಡುಗೆಗಳು ಇಂದಿಗೂ ಸ್ಫೂರ್ತಿ ನೀಡುತ್ತಿವೆ."
                : "H.H. Sri Vishwothama Theertha Swamiji (1934-2007) was one of the most revered pontiffs of Sode Vadiraja Mutt, renowned for his profound scholarship, unwavering discipline, and deep spiritual wisdom. Ascending the Peetha at a young age, he dedicated over six decades to preserving and propagating the Dvaita philosophy through rigorous teaching, manuscript preservation, temple restoration, and the establishment of institutions such as Bhavisameera Gurukula. His compassionate guidance, Mantrakshata blessings, and humble, serene nature made him a beacon of hope for countless devotees. His legacy continues to inspire through his contributions to spiritual learning, goshala development, and the preservation of the sacred traditions of Sri Vadiraja Teertha.",
            },
            {
              testid: "pontiff-current",
              badge: lang === "kn" ? "ಪ್ರಸ್ತುತ ಪೀಠಾಧಿಪತಿಗಳು" : "Current Peethadhipathi",
              years: lang === "kn" ? "೨೦೦೬ರಿಂದ" : "Since 2006",
              name: lang === "kn" ? "ಶ್ರೀ ಶ್ರೀ ವಿಶ್ವವಲ್ಲಭ ತೀರ್ಥ ಸ್ವಾಮಿಜಿ" : "H.H. Sri Vishwavallabha Theertha Swamiji",
              title: lang === "kn" ? "ಪ್ರಸ್ತುತ ಪೀಠಾಧಿಪತಿ" : "The present Peethadhipathi",
              image: require("../assets/ShreeVishwaVallabha.png"),
              text: lang === "kn"
                ? "ಶ್ರೀ ಶ್ರೀ ವಿಶ್ವವಲ್ಲಭ ತೀರ್ಥ ಸ್ವಾಮಿಜಿ, ಸೋದೆ ವಾದಿರಾಜ ಮಠದ ಪ್ರಸ್ತುತ ಪೀಠಾಧಿಪತಿಗಳು, ತಮ್ಮ ಗುರುಗಳಾದ ಶ್ರೀ ಶ್ರೀ ವಿಶ್ವೋತ್ತಮ ತೀರ್ಥ ಸ್ವಾಮಿಜಿಯವರ ಉಜ್ಜ್ವಲ ಪರಂಪರೆಯನ್ನು ಅಚಲ ಭಕ್ತಿ ಮತ್ತು ದೂರದೃಷ್ಟಿಯುಳ್ಳ ನಾಯಕತ್ವದೊಂದಿಗೆ ಮುಂದೊಯ್ಯುತ್ತಿದ್ದಾರೆ. ೨೦೦೬ರಲ್ಲಿ ಸನ್ಯಾಸ ಸ್ವೀಕರಿಸಿ, ಅವರು ಆಧ್ಯಾತ್ಮಿಕ ಪರಂಪರೆಗಳ ಬಲವರ್ಧನೆಗೆ, ಶಿಕ್ಷಣ, ಸಂಶೋಧನೆ ಹಾಗೂ ಸಮಾಜ ಸೇವೆಗೆ ತಮ್ಮನ್ನು ಸಮರ್ಪಿಸಿಕೊಂಡಿದ್ದಾರೆ. ಅವರ ಮಾರ್ಗದರ್ಶನದಲ್ಲಿ ಮಠವು ದೇವಸ್ಥಾನ ಜೀರ್ಣೋದ್ಧಾರ, ಸಂಶೋಧನೆ-ಪ್ರಕಟಣಾ ಕೇಂದ್ರಗಳ ಸ್ಥಾಪನೆ, ಶೈಕ್ಷಣಿಕ ಸಂಸ್ಥೆಗಳ ವಿಸ್ತರಣೆ, ಅಮೂಲ್ಯ ಗ್ರಂಥಗಳ ಸಂರಕ್ಷಣೆ ಮತ್ತು ವೈದಿಕ ಶಿಕ್ಷಣದ ಪ್ರಚಾರ - ಇವನ್ನೆಲ್ಲ ಸಾಧಿಸಿದೆ. ಮಹಾ ಧಾರ್ಮಿಕ ಆಚರಣೆಗಳು, ಕರುಣಾಮಯ ಸೇವೆಗಳು ಹಾಗೂ ಮಧ್ವ ಸಿದ್ಧಾಂತದ ಪ್ರಚಾರದ ಮೂಲಕ ಸ್ವಾಮಿಜಿಯವರು ಭಕ್ತರಿಗೆ ಸ್ಫೂರ್ತಿ ನೀಡುತ್ತಾ ಶ್ರೀ ಹರಿ, ವಾಯು ಹಾಗೂ ಶ್ರೀ ವಾದಿರಾಜ ತೀರ್ಥರ ಶಾಶ್ವತ ಮೌಲ್ಯಗಳನ್ನು ಎತ್ತಿಹಿಡಿಯುತ್ತಿದ್ದಾರೆ."
                : "H.H. Sri Vishwavallabha Theertha Swamiji, the present Peethadhipathi of Sode Vadiraja Mutt, carries forward the illustrious legacy of his Guru, H.H. Sri Vishwothama Theertha Swamiji, with unwavering devotion and visionary leadership. Initiated into Sannyasa in 2006, he has dedicated himself to strengthening spiritual traditions while embracing education, research, and social service. Under his guidance, the Mutt has undertaken major temple renovations, established research and publication centers, expanded educational institutions, preserved invaluable manuscripts, and promoted Vedic learning. Through grand religious celebrations, compassionate service, and the propagation of Madhva Siddhanta, Swamiji continues to inspire devotees and uphold the timeless values of Sri Hari, Vayu, and Sri Vadiraja Teertha.",
            },
          ].map((p) => (
            <article
              key={p.testid}
              className="group relative rounded-3xl overflow-hidden bg-[#FBF7F0] border border-[#D4AF37]/30 card-lift"
              data-testid={p.testid}
            >
              {/* Portrait */}
              <div className="relative aspect-[4/3] overflow-hidden bg-[#F2E7D5]">
                <img
                  src={p.image}
                  alt={p.name}
                  className="w-full h-full object-cover object-top transition-transform duration-700 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#3B2A1A]/60 via-transparent to-transparent" />
                <div className="absolute bottom-4 right-4 font-cinzel text-[10px] tracking-widest text-[#FBF7F0] bg-[#3B2A1A]/70 backdrop-blur-sm px-3 py-1.5 rounded-full">
                  {p.years}
                </div>
              </div>

              {/* Body */}
              <div className="p-7 sm:p-8 relative">
                <div className="absolute -top-8 -right-8 opacity-25 pointer-events-none">
                  <Mandala size={180} color="#D4AF37" opacity={0.6} />
                </div>
                <div className="overline text-[#B65A14] mb-2">{p.title}</div>
                <h3 className="font-serif-display text-2xl sm:text-3xl text-[#3B2A1A] leading-tight">
                  {p.name}
                </h3>
                <div className="mt-4 gold-underline" />
                <p className="mt-5 text-sm text-[#3B2A1A]/85 leading-relaxed" style={{ textAlign: "justify" }}>
                  {p.text}
                </p>
              </div>
            </article>
          ))}
        </div>

        {/* Blessing quote */}
        <div className="mt-14 max-w-3xl mx-auto text-center">
          <p className="font-serif-display text-lg sm:text-xl italic text-[#B65A14] leading-relaxed">
            {lang === "kn" ? '"ಶ್ರೀ ಹರಿ, ವಾಯು ಹಾಗೂ ಶ್ರೀ ವಾದಿರಾಜ ತೀರ್ಥರ ಆಶೀರ್ವಾದವು ಭಕ್ತರನ್ನು ಧರ್ಮದ ಶಾಶ್ವತ ಮಾರ್ಗದಲ್ಲಿ ಸದಾ ಮಾರ್ಗದರ್ಶಿಸಲಿ."' : `"May the blessings of Sri Hari, Vayu and Sri Vadiraja Teertha continue to guide devotees along the eternal path of Dharma."`}
          </p>
        </div>
      </section>

      <SectionDivider variant="lotus" />

      {/* FEATURED DEITIES */}
      <section className="py-16 relative" data-testid="home-deities" style={{ background: "linear-gradient(180deg, #E4D3B1 0%, #DFCCA6 100%)" }}>
        <div className="max-w-7xl mx-auto px-4 lg:px-8">
          <div className="flex items-end justify-between flex-wrap gap-6 mb-10">
            <SectionHeading
              eyebrow={t("home.deities.eyebrow")}
              title={t("home.deities.title")}
              subtitle={t("home.deities.subtitle")}
            />
            <Link to="/deities" className="inline-flex items-center gap-2 text-[#B65A14] font-semibold text-sm" data-testid="deities-view-all">
              {t("home.deities.viewAll")} <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {featuredDeities.map((d) => (
              <Link
                key={d.slug}
                to="/deities"
                className="group relative rounded-2xl overflow-hidden border border-[#D4AF37]/30 card-lift bg-[#FBF7F0]"
                data-testid={`deity-card-${d.slug}`}
              >
                <div className="aspect-square overflow-hidden relative">
                  {d.isIdol ? (
                    <div className="absolute inset-0 bg-gradient-to-b from-[#FBF7F0] via-[#F2E7D5] to-[#E4D3B1] flex items-center justify-center">
                      <div className="absolute inset-0 flex items-center justify-center opacity-45 animate-mandala-slow">
                        <Mandala size={280} color="#B65A14" opacity={0.5} />
                      </div>
                      <div className="absolute inset-0" style={{ background: "radial-gradient(circle at 50% 45%, rgba(201,162,39,0.30) 0%, transparent 65%)" }} />
                      {d.image ? (
                        <img
                          src={d.image}
                          alt={d.name}
                          className={`relative z-10 object-contain transition-transform duration-700 group-hover:scale-105 ${d.slug === "bhuvaraha" ? "max-h-[62%] max-w-[52%]" : "max-h-[100%] max-w-[100%]"}`}
                          style={{ filter: "drop-shadow(0 10px 22px rgba(51, 36, 28, 0.4))" }}
                        />
                      ) : (
                        <div className="relative z-10 text-center px-4">
                          <div className="w-20 h-20 mx-auto rounded-full border-2 border-[#B65A14]/60 flex items-center justify-center mb-3 bg-[#FBF7F0]/80">
                            <Sparkles className="w-8 h-8 text-[#B65A14]" />
                          </div>
                          <div className="font-serif-display text-base text-[#B65A14]">Awaiting Darshana</div>
                        </div>
                      )}
                    </div>
                  ) : (
                    <>
                      <img src={d.image} alt={d.name} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" />
                      <div className="absolute inset-0 bg-gradient-to-t from-[#3B2A1A] via-[#3B2A1A]/30 to-transparent" />
                    </>
                  )}
                </div>
                <div className="bg-gradient-to-b from-[#B65A14] to-[#8F4610] px-4 py-3 relative z-20">
                  {/* <div className="font-cinzel text-xs tracking-widest text-[#D4AF37] mb-0.5 text-center">{d.sanskrit}</div> */}
                  <div className="font-serif-display text-xl sm:text-2xl text-[#FBF7F0] font-semibold leading-tight text-center">{pickDeity(d, "name")}</div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <SectionDivider variant="arch" />

      {/* RECENT EVENTS */}
      <section className="bg-sand-texture py-16 relative overflow-hidden" data-testid="home-events">
        <div className="absolute -top-24 -right-24 pointer-events-none animate-mandala-slow"><Mandala size={360} color="#B65A14" opacity={0.25} /></div>
        <div className="max-w-7xl mx-auto px-4 lg:px-8">
          <div className="flex items-end justify-between flex-wrap gap-6 mb-10">
            <SectionHeading
              eyebrow={lang === "kn" ? "ಕಾರ್ಯಕ್ರಮಗಳು ಮತ್ತು ಆಚರಣೆಗಳು" : "Events & Celebrations"}
              title={lang === "kn" ? "ಇತ್ತೀಚಿನ ಕಾರ್ಯಕ್ರಮಗಳು" : "Recent Events"}
              subtitle={lang === "kn" ? "ಉತ್ಸವಗಳು, ಪ್ರವಚನಗಳು, ಸಾಂಸ್ಕೃತಿಕ ಸಂಜೆಗಳು ಹಾಗೂ ಸಮುದಾಯ ಕಾರ್ಯಕ್ರಮಗಳು - ಸೋದೆ ಮಠದ ದೈನಂದಿನ ಜೀವನದ ಸ್ಪಂದನ." : "Festivals, discourses, cultural events and community programmes - the pulse of daily and seasonal life at Sode Matha."}
            />
            <Link to="/events" className="inline-flex items-center gap-2 text-[#B65A14] font-semibold text-sm" data-testid="events-view-all">
              {lang === "kn" ? "ಎಲ್ಲಾ ಕಾರ್ಯಕ್ರಮಗಳನ್ನು ನೋಡಿ" : "View all events"} <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
          {recentEventsLoading && (
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5" aria-live="polite">
              {Array.from({ length: 4 }).map((_, index) => (
                <div key={index} className="bg-[#FBF7F0] border border-[#D4AF37]/30 rounded-2xl overflow-hidden flex flex-col animate-pulse">
                  <div className="aspect-[16/10] bg-[#F2E7D5]" />
                  <div className="p-4 space-y-3">
                    <div className="h-3 w-24 rounded bg-[#D4AF37]/30" />
                    <div className="h-5 w-full rounded bg-[#3B2A1A]/10" />
                    <div className="h-3 w-3/4 rounded bg-[#3B2A1A]/10" />
                  </div>
                </div>
              ))}
            </div>
          )}
          {!recentEventsLoading && recentEventsError && (
            <div className="rounded-2xl border border-[#D4AF37]/30 bg-[#FBF7F0] px-5 py-4 text-sm text-[#3B2A1A]/80">
              {recentEventsError}
            </div>
          )}
          {!recentEventsLoading && !recentEventsError && (
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
              {recentEvents.map((e) => (
                <Link
                  key={e.id}
                  to={`/events/${e.slug}`}
                  className="bg-[#FBF7F0] border border-[#D4AF37]/30 rounded-2xl overflow-hidden card-lift flex flex-col"
                  data-testid={`home-event-${e.slug}`}
                >
                  <div className="aspect-[16/10] overflow-hidden">
                    <img src={e.thumbnail || IMG.processional} alt={e.title} className="w-full h-full object-cover hover:scale-105 transition-transform duration-500" />
                  </div>
                  <div className="p-4 flex-1 flex flex-col">
                    <div className="text-xs font-semibold text-[#B65A14]">
                      {e.date ? new Date(e.date).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" }) : ""}
                    </div>
                    <div className="font-serif-display text-lg text-[#3B2A1A] mt-1 leading-snug">{e.title}</div>
                    <div className="text-xs text-[#3B2A1A]/70 italic mt-0.5 line-clamp-2">{e.subtitle}</div>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* TEMPLE TIMINGS */}
      <section className="py-16" data-testid="home-timings">
        <div className="max-w-7xl mx-auto px-4 lg:px-8 grid lg:grid-cols-2 gap-14 items-center">
          <div>
            <SectionHeading
              eyebrow={lang === "kn" ? "ದೈನಂದಿನ ದರ್ಶನ" : "Daily Darshana"}
              title={lang === "kn" ? "ದೇವಸ್ಥಾನ ಸಮಯಗಳು ಮತ್ತು ದೈನಂದಿನ ಪೂಜೆ" : "Temple timings & daily worship"}
              subtitle={lang === "kn" ? "ಸೋದೆ ಮಠದ ದೈನಂದಿನ ಪೂಜಾ ಲಯಕ್ಕೆ ಅನುಗುಣವಾಗಿ ನಿಮ್ಮ ಭೇಟಿಯನ್ನು ಯೋಜಿಸಿ." : "Plan your visit around the daily rhythm of worship at Sode Matha."}
            />
            <div className="mt-8 space-y-3">
              {TEMPLE_TIMINGS.map((tm) => (
                <div key={tm.period} className="flex items-start gap-4 bg-[#FBF7F0] border border-[#D4AF37]/25 rounded-2xl p-5">
                  <div className="w-11 h-11 rounded-full bg-[#F2E7D5] text-[#B65A14] flex items-center justify-center">
                    <Clock className="w-5 h-5" />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center justify-between flex-wrap gap-2">
                      <div className="font-serif-display text-lg text-[#3B2A1A]">{lang === "kn" ? tm.periodKn : tm.period}</div>
                      <div className="font-cinzel text-xs tracking-widest text-[#B65A14]">{lang === "kn" ? tm.timeKn : tm.time}</div>
                    </div>
                    <div className="text-sm text-[#3B2A1A]/70 mt-1">{lang === "kn" ? tm.noteKn : tm.note}</div>
                  </div>
                </div>
              ))}
            </div>
            <Link to="/contact" className="mt-8 inline-flex items-center gap-2 text-[#B65A14] font-semibold" data-testid="timings-more">
              {lang === "kn" ? "ದೈನಂದಿನ ಪೂಜೆಯನ್ನು ಅನ್ವೇಷಿಸಿ" : "Explore daily worship"} <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
          <div className="relative flex justify-center">
            <div className="aspect-square w-full max-w-[350px] sm:max-w-[400px] lg:max-w-[450px] rounded-3xl overflow-hidden shadow-2xl">
              <img
                src={require("../assets/xlil7tg9_sripanchaBrindavana.webp")}
                alt="Sri Pancha Brindavana adorned with garlands and lamps during daily worship"
                className="w-full h-full object-cover"
                data-testid="timings-image"
              />
            </div>
            <div className="absolute -top-6 -right-6 pointer-events-none">
              <Mandala size={200} color="#D4AF37" opacity={0.5} />
            </div>
          </div>
        </div>
      </section>

      {/* FEATURED PILLARS */}
      <section className="bg-gradient-to-b from-[#F2E7D5] to-[#EBDBB8] text-[#3B2A1A] relative overflow-hidden py-16" data-testid="home-pillars">
        <div className="absolute -bottom-24 -left-24 opacity-25 pointer-events-none animate-mandala-slow"><Mandala size={340} color="#B65A14" opacity={1} /></div>
        <div className="max-w-7xl mx-auto px-4 lg:px-8">
          <SectionHeading
            eyebrow={t("home.explore.eyebrow")}
            title={t("home.pillars.eyebrow")}
            subtitle=""
            align="center"
          />
          <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {[
              {
                icon: HandHeart,
                title: "Goseva",
                titleKn: "ಗೋಸೇವೆ",
                desc: "Care and protection of holy cows through the Kamadhenu Trust - a sacred seva that sustains the Matha's traditional Goshala.",
                descKn: "ಕಾಮಧೇನು ಟ್ರಸ್ಟ್ ಮೂಲಕ ಪವಿತ್ರ ಗೋವುಗಳ ಸಂರಕ್ಷಣೆ ಹಾಗೂ ಪೋಷಣೆ - ಮಠದ ಸಾಂಪ್ರದಾಯಿಕ ಗೋಶಾಲೆಯನ್ನು ಉಳಿಸಿಕೊಂಡಿರುವ ಪವಿತ್ರ ಸೇವೆ.",
                url: "https://www.kamadhenutrust.com/",
                img: require("../assets/m4i18u4o_goseva.jpg"),
              },
              {
                icon: BookOpen,
                title: "Publications",
                titleKn: "ಪ್ರಕಟಣೆಗಳು",
                desc: "Tatvamallika - the monthly magazine of Sri Sode Vadiraja Matha featuring discourses, articles and classical granthas.",
                descKn: "ತತ್ವಮಲ್ಲಿಕಾ - ಪ್ರವಚನಗಳು, ಲೇಖನಗಳು ಹಾಗೂ ಶಾಸ್ತ್ರೀಯ ಗ್ರಂಥಗಳನ್ನೊಳಗೊಂಡ ಶ್ರೀ ಸೋದೆ ವಾದಿರಾಜ ಮಠದ ಮಾಸಿಕ ಪತ್ರಿಕೆ.",
                url: "https://www.tatwamallika.com/",
                img: require("../assets/fr4qf62x_publications.jpeg"),
              },
              {
                icon: ScrollText,
                title: "Vedic E-Document",
                titleKn: "ವೈದಿಕ ಇ-ಗ್ರಂಥ",
                desc: "Digital archive of Vedic manuscripts, granthas and rare literature preserved by the Sarvamoola Vedic Society (SVSPK).",
                descKn: "ಸರ್ವಮೂಲ ವೈದಿಕ ಸಂಸ್ಥೆ (ಎಸ್‌ವಿಎಸ್‌ಪಿಕೆ) ಸಂರಕ್ಷಿಸಿದ ವೈದಿಕ ಗ್ರಂಥಗಳು, ಶಾಸ್ತ್ರಗಳು ಹಾಗೂ ಅಪರೂಪದ ಸಾಹಿತ್ಯದ ಡಿಜಿಟಲ್ ಆರ್ಕೈವ್.",
                url: "https://www.svspk.in/",
                img: require("../assets/ru2h17yb_veda_edovument.webp"),
              },
              {
                icon: GraduationCap,
                title: "Education Institutions",
                titleKn: "ಶೈಕ್ಷಣಿಕ ಸಂಸ್ಥೆಗಳು",
                desc: "Gurukulas, Sanskrit patashalas, schools and colleges founded and supported by the Matha across Karnataka.",
                descKn: "ಕರ್ನಾಟಕದಾದ್ಯಂತ ಮಠವು ಸ್ಥಾಪಿಸಿ ಪೋಷಿಸುತ್ತಿರುವ ಗುರುಕುಲಗಳು, ಸಂಸ್ಕೃತ ಪಾಠಶಾಲೆಗಳು, ಶಾಲೆಗಳು ಹಾಗೂ ಕಾಲೇಜುಗಳು.",
                url: "https://sode-edu.in/",
                img: require("../assets/nl91yvnj_SMVIT_building.png"),
              },
            ].map((p) => (
              <button
                key={p.title}
                onClick={() => setPillarModal(p)}
                className="group text-left relative rounded-2xl overflow-hidden border border-[#D4AF37]/25 card-lift"
                data-testid={`pillar-${p.title.toLowerCase().replace(/\s/g, "-")}`}
              >
                <div className="aspect-[4/5] overflow-hidden">
                  <img src={p.img} alt={p.title} className="w-full h-full object-cover opacity-100 group-hover:scale-110 transition-transform duration-700" />
                  <div className={`absolute inset-0 ${p.overlayClass || "bg-gradient-to-t from-[#3B2A1A]/90 via-[#3B2A1A]/25 to-transparent"}`} />
                </div>
                <div className="absolute inset-0 flex flex-col justify-end p-6">
                  <p.icon className="w-6 h-6 text-[#D4AF37] mb-3" />
                  <h3 className={`font-serif-display text-2xl mb-1 ${p.titleClass || "text-[#FBF7F0]"}`}>{lang === "kn" ? p.titleKn : p.title}</h3>
                  <p className={`text-sm leading-relaxed line-clamp-3 ${p.descClass || "text-[#F2E7D5]/85"}`}>{lang === "kn" ? p.descKn : p.desc}</p>
                  <div className="mt-4 text-xs uppercase tracking-widest text-[#D4AF37] flex items-center gap-2">
                    {t("common.learnMore")} <ArrowRight className="w-3 h-3" />
                  </div>
                </div>
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Pillar detail modal */}
      <Dialog open={!!pillarModal} onOpenChange={(o) => !o && setPillarModal(null)}>
        <DialogContent className="max-w-lg bg-[#FBF7F0] border-[#D4AF37]/40" data-testid="pillar-dialog">
          {pillarModal && (
            <>
              <DialogHeader>
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-11 h-11 rounded-full bg-[#F2E7D5] text-[#B65A14] flex items-center justify-center">
                    <pillarModal.icon className="w-5 h-5" />
                  </div>
                  <DialogTitle className="font-serif-display text-2xl text-[#3B2A1A]">
                    {lang === "kn" ? pillarModal.titleKn : pillarModal.title}
                  </DialogTitle>
                </div>
              </DialogHeader>
              <DialogDescription className="text-sm text-[#3B2A1A]/85 leading-relaxed" data-testid="pillar-desc">
                {lang === "kn" ? pillarModal.descKn : pillarModal.desc}
              </DialogDescription>
              <div className="mt-6 flex flex-wrap gap-3">
                <a
                  href={pillarModal.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 rounded-full bg-[#B65A14] text-[#FBF7F0] px-6 py-3 text-sm font-semibold hover:bg-[#8F4610] transition"
                  data-testid="pillar-know-more"
                >
                  {lang === "kn" ? "ಇನ್ನಷ್ಟು ತಿಳಿಯಿರಿ" : "Know More"} <ArrowRight className="w-4 h-4" />
                </a>
                <button
                  onClick={() => setPillarModal(null)}
                  className="inline-flex items-center gap-2 rounded-full border border-[#D4AF37]/50 text-[#3B2A1A] px-6 py-3 text-sm font-semibold hover:bg-[#F2E7D5]"
                  data-testid="pillar-close"
                >
                  {t("common.cancel")}
                </button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* YOUTUBE - Latest videos from Matha channel */}
      <section className="py-16" data-testid="home-videos">
        <div className="max-w-7xl mx-auto px-4 lg:px-8">
          <div className="flex items-end justify-between flex-wrap gap-6 mb-10">
            <SectionHeading
              eyebrow={lang === "kn" ? "ಮಠದಿಂದ" : "From the Matha"}
              title={lang === "kn" ? "ಇತ್ತೀಚಿನ ವೀಡಿಯೊಗಳು" : "Latest videos"}
              subtitle={lang === "kn" ? "ಪ್ರವಚನಗಳು, ಉತ್ಸವದ ವಿಶೇಷಾಂಶಗಳು, ವರ್ಚುವಲ್ ದರ್ಶನಗಳು ಮತ್ತು ಇನ್ನೂ ಹೆಚ್ಚು - ನಮ್ಮ ಅಧಿಕೃತ ಚಾನೆಲ್‌ನಿಂದ ನೇರವಾಗಿ." : "Discourses, festival highlights, virtual darshanas and more - direct from our official channel."}
            />
            <a
              href={YOUTUBE_CHANNEL_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 text-[#B65A14] font-semibold text-sm"
              data-testid="youtube-channel-link"
            >
              {lang === "kn" ? "ಚಾನೆಲ್ ಭೇಟಿ ಮಾಡಿ" : "Visit channel"} <ArrowRight className="w-4 h-4" />
            </a>
          </div>
          {videos.length > 0 ? (
            <div className="grid md:grid-cols-2 xl:grid-cols-4 gap-5">
              {videos.map((v) => (
                <a
                  key={v.id}
                  href={`https://www.youtube.com/watch?v=${v.id}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group relative bg-[#FBF7F0] border border-[#D4AF37]/25 rounded-2xl overflow-hidden card-lift"
                  data-testid={`home-video-${v.id}`}
                >
                  <div className="aspect-video overflow-hidden relative">
                    <img
                      src={ytThumbnail(v.id)}
                      alt={v.title}
                      className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                    />
                    <div className="absolute inset-0 bg-[#3B2A1A]/30 flex items-center justify-center">
                      <div className="w-14 h-14 rounded-full bg-[#B65A14] flex items-center justify-center shadow-xl">
                        <PlayCircle className="w-8 h-8 text-[#FBF7F0]" />
                      </div>
                    </div>
                    {v.duration && (
                      <div className="absolute bottom-2 right-2 text-[10px] font-semibold text-[#FBF7F0] bg-[#3B2A1A]/80 rounded px-2 py-0.5">
                        {v.duration}
                      </div>
                    )}
                    {v.isLive && (
                      <div className="absolute top-3 right-3 rounded-full bg-[#C62828] px-3 py-1 text-[10px] font-bold uppercase tracking-[0.2em] text-white shadow-lg">
                        Live
                      </div>
                    )}
                  </div>
                  <div className="p-5">
                    <h3 className="font-serif-display text-lg text-[#3B2A1A] leading-snug line-clamp-2" style={{ fontFamily: "'Noto Sans Kannada', 'Cormorant Garamond', serif" }}>{v.title}</h3>
                    {v.publishedAt && (
                      <div className="text-xs text-[#3B2A1A]/60 mt-2">
                        {new Date(v.publishedAt).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })}
                      </div>
                    )}
                  </div>
                </a>
              ))}
            </div>
          ) : (
            <div className="rounded-2xl border border-[#D4AF37]/25 bg-[#FBF7F0] px-6 py-8 text-center text-sm text-[#3B2A1A]/75">
              {lang === "kn" ? "ಇತ್ತೀಚಿನ ವೀಡಿಯೊಗಳು ಈಗ ಲಭ್ಯವಿಲ್ಲ." : "Latest videos are not available right now."}
            </div>
          )}
        </div>
      </section>

      {/* CTA */}
      <CTASection
        eyebrow={lang === "kn" ? "ಪವಿತ್ರ ಪ್ರವಾಸಕ್ಕೆ ಸೇರಿಕೊಳ್ಳಿ" : "Join the sacred journey"}
        title={lang === "kn" ? "ಪ್ರತಿ ಸಮರ್ಪಣೆ, ಪ್ರತಿ ಪ್ರಾರ್ಥನೆ, ಪ್ರತಿ ಭೇಟಿಯೂ - ಶತಮಾನಗಳ ಪರಂಪರೆಯ ಭಾಗವಾಗುತ್ತದೆ." : "Every offering, every prayer, every visit - becomes part of a centuries-old tradition."}
        subtitle={lang === "kn" ? "ಶ್ರೀ ಸೋದೆ ವಾದಿರಾಜ ಮಠದ ದೈನಂದಿನ ಪೂಜೆ, ವಿದ್ವತ್ ಮತ್ತು ಸಮಾಜ ಸೇವೆಗೆ ಸಹಕರಿಸಿ." : "Support the daily worship, scholarship and community service of Sri Sode Vadiraja Matha."}
        primary={{ label: t("nav.bookSeva"), to: "/sevas" }}
        secondary={{ label: lang === "kn" ? "ನಿಮ್ಮ ಭೇಟಿಯನ್ನು ಯೋಜಿಸಿ" : "Plan Your Visit", to: "/contact" }}
        image={IMG.processional}
      />
    </PageLayout>
  );
};

export default Home;
